import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import generalLogo from '../assets/JakartaLogo.svg';

const getRouteForChild = (childName) => {
  const childLower = childName.toLowerCase();
  if (childLower.includes('history') || childLower.includes('sejarah')) return 'about';
  if ((childLower.includes('vision') || childLower.includes('visi')) && !childLower.includes('visit')) return 'vision-mission';
  if (childLower.includes('gandhi')) return 'gandhian-values';
  if (childLower.includes('newsletter') || childLower.includes('buletin')) return 'newsletter';
  if (childLower.includes('latest news') || childLower.includes('berita terbaru')) return 'latest-news';
  if (childLower.includes('faq')) return 'faqs';
  if (
    childLower.includes('accreditation') ||
    childLower.includes('akreditasi') ||
    childLower.includes('licence') ||
    childLower.includes('lisensi')
  ) return 'accreditation-licences';
  if (
    childLower.includes('curriculum') ||
    childLower.includes('kurikulum') ||
    childLower.includes('approach') ||
    childLower.includes('pendekatan')
  ) return 'curriculum-approach';
  if (childLower.includes('leadership & faculty') || childLower.includes('kepemimpinan & staf')) return 'leadership-faculty';
  if (childLower.includes('facilities') || childLower.includes('fasilitas')) return 'school-facilities';
  if (childLower.includes('preschool') || childLower.includes('prasekolah')) return 'preschool';
  if (childLower.includes('primary school') || childLower.includes('sekolah dasar')) return 'primary-school';
  if (childLower.includes('middle school') || childLower.includes('sekolah menengah') || childLower.includes('myp')) return 'middle-school';
  if (childLower.includes('senior school') || childLower.includes('sekolah atas') || childLower.includes('ib dp') || childLower.includes('ib cp')) return 'senior-school';
  if (childLower.includes('calendar') || childLower.includes('kalender')) return 'academic-calendar';
  if (childLower.includes('school events') || childLower.includes('acara sekolah')) return 'school-events';
  if (
    childLower.includes('well-being') ||
    childLower.includes('wellbeing') ||
    childLower.includes('safeguarding') ||
    childLower.includes('kesejahteraan') ||
    childLower.includes('perlindungan')
  ) return 'well-being-safeguarding';
  if (
    childLower.includes('leadership') ||
    childLower.includes('kepemimpinan')
  ) return 'student-leadership';
  if (
    childLower.includes('play production') ||
    childLower.includes('produksi teater') ||
    childLower.includes('teater') ||
    childLower.includes('theatre')
  ) return 'play-production';
  if (
    childLower.includes('alumni') ||
    childLower.includes('komunitas')
  ) return 'alumni-community';
  if (
    childLower.includes('house system') ||
    childLower.includes('house')
  ) return 'house-system';
  if (
    childLower.includes('extracurricular') ||
    childLower.includes('ekstrakurikuler')
  ) return 'extracurricular-activities';
  if (
    childLower.includes('admissions process') ||
    childLower.includes('proses penerimaan') ||
    childLower.includes('admission process') ||
    childLower.includes('process') ||
    childLower.includes('proses')
  ) return 'admissions-process';
  if (
    childLower.includes('fees') ||
    childLower.includes('tuition') ||
    childLower.includes('biaya') ||
    childLower.includes('spp')
  ) return 'fees-tuition';
  if (
    childLower.includes('schedule a visit') ||
    childLower.includes('jadwalkan kunjungan') ||
    childLower.includes('visit') ||
    childLower.includes('kunjungan')
  ) return 'schedule-visit';
  if (childLower.includes('contact') || childLower.includes('hubungi')) return 'contact';
  return 'coming-soon';
};

const MainNav = ({ t, lang, setLang, page, setPage }) => {
  const [visible, setVisible] = useState(true);
  const [atTop, setAtTop]     = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMobileItem, setExpandedMobileItem] = useState(null);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const currentY = window.scrollY;
      setAtTop(currentY < 10);
      if (currentY < 10) {
        setVisible(true);
      } else if (currentY > lastY + 4) {
        // scrolling down — hide
        setVisible(false);
      } else if (currentY < lastY - 4) {
        // scrolling up — show
        setVisible(true);
      }
      lastY = currentY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavigate = (targetRoute) => {
    if (setPage) setPage(targetRoute);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
    <div
      className={`w-full bg-white border-b border-line fixed left-0 right-0 z-50 transition-all duration-300 ease-in-out
        ${atTop ? 'top-0 lg:top-12' : 'top-0'}
        ${visible ? 'translate-y-0 shadow-sm' : '-translate-y-full'}
        ${atTop ? 'shadow-none' : 'shadow-md'}
      `}
    >
    <div className="max-w-[1440px] mx-auto px-5 lg:px-8 xl:px-[120px] h-16 lg:h-[88px] box-border flex items-center gap-4">
      
      {/* Logo */}
      <a href="#" onClick={(e) => { e.preventDefault(); handleNavigate('home'); }} aria-label="GMIS Jakarta home" className="flex items-center gap-3 lg:gap-0 shrink-0">
        <img src={generalLogo} alt="GMIS Logo" className="h-[46px] lg:h-[72px] w-auto hidden lg:block transition-all duration-200" />
        
        {/* Mobile Logo Text */}
        <div className="flex lg:hidden items-center gap-3">
          <span className="font-heading font-semibold text-[34px] leading-none text-navy tracking-[0.02em]">GMIS</span>
          <span className="w-px h-8 bg-gold"></span>
          <span className="text-[11px] font-semibold tracking-[0.2em] text-navy">JAKARTA</span>
        </div>
      </a>

      {/* Desktop Nav links */}
      <nav className="ml-auto hidden lg:flex items-center gap-5 lg:gap-6 xl:gap-8 h-[88px]">
        {t.nav.map((item) => {
          const isContact = item.label.toLowerCase().includes('contact');
          const isAbout = item.label.toLowerCase().includes('about');
          const targetPage = isContact ? 'contact' : isAbout ? 'about' : 'home';
          const isActive = page === targetPage;
          const hasChildren = item.children && item.children.length > 0;
          
          return (
            <div key={item.label} className="relative group h-[88px] flex items-center">
              <a
                className={`navlink flex items-center whitespace-nowrap h-full box-border pt-0.5 text-[12px] xl:text-[14.5px] font-semibold text-ink cursor-pointer ${isActive && (!hasChildren || targetPage === 'about') ? 'border-b-2 border-gold' : ''}`}
                href="#"
                onClick={(e) => { 
                  if (!hasChildren || isContact) {
                    e.preventDefault(); 
                    handleNavigate(targetPage);
                  }
                }}
              >
                {item.label}
                {hasChildren && (
                  <svg className="ml-1.5 opacity-50 transition-transform group-hover:rotate-180 shrink-0" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                )}
              </a>

              {hasChildren && (
                <div className="absolute top-[88px] left-1/2 -translate-x-1/2 min-w-[280px] bg-white/95 backdrop-blur-md border-x border-b border-line rounded-[8px] rounded-t-none shadow-[0_20px_40px_rgba(14,36,62,0.08)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 flex flex-col pt-1 pb-3 translate-y-3 group-hover:translate-y-0 before:content-[''] before:absolute before:h-6 before:-top-6 before:left-0 before:w-full overflow-hidden">
                  
                  <div className="flex flex-col mt-2 px-2">
                    {item.children.map(child => {
                      const targetRoute = getRouteForChild(child);
                      return (
                        <a 
                          key={child}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleNavigate(targetRoute);
                          }}
                          className="group/item relative px-5 py-3 text-[13.5px] font-medium text-ink hover:text-navy transition-all duration-200 rounded-[4px] overflow-hidden"
                        >
                          <div className="absolute inset-0 bg-paper opacity-0 group-hover/item:opacity-100 transition-opacity duration-200" />
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-gold group-hover/item:h-3/5 transition-all duration-300 rounded-r-[2px]" />
                          <span className="relative z-10 flex items-center justify-between">
                            <span className="transform transition-transform duration-200 group-hover/item:translate-x-1">{child}</span>
                            <svg className="opacity-0 -translate-x-2 transition-all duration-200 group-hover/item:opacity-100 group-hover/item:translate-x-0 text-gold" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                            </svg>
                          </span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <button
        type="button"
        className="hidden lg:flex shrink-0 whitespace-nowrap btn items-center h-[38px] px-4 xl:px-5 rounded-[4px] bg-navy text-white text-[11px] xl:text-[11.5px] font-bold tracking-wider uppercase cursor-pointer hover:bg-navy-deep transition-all duration-200 border-0"
        onClick={() => {
          handleNavigate('schedule-visit');
        }}
      >
        {t.visit}
      </button>

      {/* Mobile Controls */}
      <div className="flex lg:hidden items-center gap-1">
        <div className="flex items-center">
          <button 
            onClick={() => setLang && setLang('en')}
            className={`h-11 min-w-[40px] px-1.5 bg-transparent border-0 border-b-2 text-[13px] tracking-[0.06em] text-navy ${lang === 'en' ? 'border-gold font-bold' : 'border-transparent font-normal'}`}>
            EN
          </button>
          <span className="text-muted opacity-50">|</span>
          <button 
            onClick={() => setLang && setLang('id')}
            className={`h-11 min-w-[40px] px-1.5 bg-transparent border-0 border-b-2 text-[13px] tracking-[0.06em] text-navy ${lang === 'id' ? 'border-gold font-bold' : 'border-transparent font-normal'}`}>
            ID
          </button>
        </div>
        <button
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-11 h-11 flex items-center justify-center bg-transparent border-0 text-navy p-0 focus:outline-none cursor-pointer"
        >
          {mobileMenuOpen ? <X size={24} strokeWidth={2} /> : <Menu size={24} strokeWidth={1.8} />}
        </button>
      </div>

    </div>
  </div>

  {/* Mobile Menu Drawer */}
  {mobileMenuOpen && (
    <div className="fixed inset-0 top-16 z-40 bg-navy/60 backdrop-blur-sm lg:hidden transition-opacity">
      <div className="bg-white h-[calc(100vh-64px)] w-full max-w-[360px] ml-auto flex flex-col justify-between overflow-y-auto shadow-2xl p-6">
        <div className="flex flex-col gap-2">
          {t.nav.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const isContact = item.label.toLowerCase().includes('contact');
            const isAbout = item.label.toLowerCase().includes('about');
            const targetPage = isContact ? 'contact' : isAbout ? 'about' : 'home';
            const isExpanded = expandedMobileItem === item.label;

            return (
              <div key={item.label} className="border-b border-line/60 pb-2">
                <div
                  className="flex items-center justify-between py-2 text-ink font-semibold text-[15px] cursor-pointer"
                  onClick={() => {
                    if (hasChildren && !isContact) {
                      setExpandedMobileItem(isExpanded ? null : item.label);
                    } else {
                      handleNavigate(targetPage);
                    }
                  }}
                >
                  <span>{item.label}</span>
                  {hasChildren && !isContact && (
                    <ChevronDown
                      size={18}
                      className={`text-gold transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  )}
                </div>

                {hasChildren && isExpanded && (
                  <div className="pl-4 py-2 flex flex-col gap-2 bg-paper/60 rounded-lg mt-1">
                    {item.children.map((child) => (
                      <button
                        key={child}
                        onClick={() => handleNavigate(getRouteForChild(child))}
                        className="text-left text-[14px] text-ink/80 hover:text-navy py-1.5 font-medium flex items-center justify-between"
                      >
                        <span>{child}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="pt-6 border-t border-line mt-6 flex flex-col gap-3">
          <div className="flex items-center justify-around py-2 border-b border-line/60 text-xs font-semibold text-ink/80">
            <button
              onClick={() => handleNavigate('coming-soon')}
              className="hover:text-navy transition-colors bg-transparent border-0 cursor-pointer text-ink/80 font-medium text-[13px]"
            >
              {lang === 'en' ? 'Career' : 'Karir'}
            </button>
            <span className="text-line">|</span>
            <button
              onClick={() => handleNavigate('coming-soon')}
              className="hover:text-navy transition-colors bg-transparent border-0 cursor-pointer text-ink/80 font-medium text-[13px]"
            >
              {lang === 'en' ? 'Parent & Student Portal' : 'Portal Orang Tua & Siswa'}
            </button>
          </div>
          <button
            onClick={() => handleNavigate('schedule-visit')}
            className="w-full py-3 bg-navy text-white text-xs font-bold uppercase tracking-wider rounded-md text-center"
          >
            {t.visit}
          </button>
          <div className="text-center text-[12px] text-ink/60">
            GMIS Jakarta • Since 1950
          </div>
        </div>
      </div>
    </div>
  )}
  </>
  );
};

export default MainNav;
